import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import * as XLSX from "xlsx";
import html2pdf from "html2pdf.js";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import {
  SortableContext, useSortable, horizontalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  MagnifyingGlass, Plus, Archive, FileXls, FilePdf, Bell, PencilSimple, DotsSixVertical, X, ArrowClockwise,
  List as ListIcon, CaretDown, CaretUp, ArrowsDownUp, Paperclip, WarningCircle,
} from "@phosphor-icons/react";
import LoanModal from "@/components/loans/LoanModal";
import ReminderConfigModal from "@/components/loans/ReminderConfigModal";
import BulkUploadModal from "@/components/loans/BulkUploadModal";
import AddColumnModal from "@/components/loans/AddColumnModal";
import RestoreColumnsModal from "@/components/loans/RestoreColumnsModal";

const LOAN_TYPE_CLASSES = ["Promissory Note", "1st Lien", "2nd Lien"];
const RATE_TYPES = ["fixed", "floating", "adjustable"];

// Column definitions. ``id`` is stable, ``label`` is display.
// ``loan_id`` is pinned first and NOT included here.
// ``calc: true`` marks a computed column (read-only, no editor).
// Same order applies to Active AND Archived tables.
const DEFAULT_COLS = [
  { id: "collateral",             label: "Collateral",              type: "text",    autocomplete: true },
  { id: "loan_type_class",        label: "Loan Type",               type: "text",    autocomplete: true },
  { id: "lender",                 label: "Lender",                  type: "text",    autocomplete: true },
  { id: "borrower",               label: "Borrower",                type: "text",    autocomplete: true },
  { id: "principal",              label: "Loan Principal",          type: "money" },
  { id: "monthly_payment",        label: "Monthly Payment",         type: "money" },
  { id: "construction_holdback",  label: "Construction Holdback Amt", type: "money" },
  { id: "origination_points",     label: "Origination Points",      type: "percent" },
  { id: "extension_points",       label: "Extension Points",        type: "percent" },
  { id: "release_amount",         label: "Release Amount",          type: "money" },
  { id: "sale_amount",            label: "Sale Price",              type: "money" },
  { id: "gross_proceeds",         label: "Gross Proceeds",          type: "money",   calc: true },
  { id: "purchase_price",         label: "Purchase Price",          type: "money" },
  { id: "current_value",          label: "Curr. Value",             type: "money" },
  { id: "equity_value",           label: "Equity Value",            type: "money",   calc: true },
  { id: "arv",                    label: "ARV",                     type: "money" },
  { id: "term_months",            label: "Term (mo)",               type: "number",  calc: true },
  { id: "rate_type",              label: "Int Type",                type: "text",    autocomplete: true },
  { id: "loan_start_date",        label: "Loan Start Date",         type: "date" },
  { id: "maturity_date",          label: "Maturity Date",           type: "date" },
  { id: "interest_rate",          label: "Int Rate",                type: "percent" },
  { id: "step_up_date",           label: "Step Up Date",            type: "date" },
  { id: "step_up_rate",           label: "Step Up Rate",            type: "percent" },
  { id: "interest_reserve",       label: "Interest Reserve",        type: "money" },
  { id: "balloon_payment",        label: "Balloon Payment",         type: "money" },
  { id: "monthly_revenue",        label: "Monthly Revenue",         type: "money" },
  { id: "monthly_expense",        label: "Monthly Expense",         type: "money" },
  { id: "monthly_tax",            label: "Monthly Tax",             type: "money" },
  { id: "cash_flow",              label: "Gross Est. Cash Flow",    type: "money",   calc: true },
];

const COL_ORDER_LS_KEY = "strata.loans.columnOrder.v4";
const KPI_ORDER_LS_KEY = "strata.loans.kpiOrder.v1";

const fmtMoney = (n) => {
  if (n == null || n === "") return "";
  const v = Number(n);
  if (!isFinite(v)) return "";
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const fmtPct = (n) => {
  if (n == null || n === "") return "";
  const v = Number(n);
  if (!isFinite(v)) return "";
  return `${v.toFixed(3).replace(/\.?0+$/, "")}%`;
};
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/** Returns { key, tone, label } for the maturity pill colour. */
function maturityTone(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { key: "expired",  cls: "bg-rose-600 text-white",  label: `${Math.abs(diffDays)}d overdue` };
  if (diffDays <= 30) return { key: "d30",    cls: "bg-rose-100 text-rose-700 border border-rose-200", label: `${diffDays}d` };
  if (diffDays <= 60) return { key: "d60",    cls: "bg-orange-100 text-orange-700 border border-orange-200", label: `${diffDays}d` };
  if (diffDays <= 90) return { key: "d90",    cls: "bg-yellow-100 text-yellow-800 border border-yellow-200", label: `${diffDays}d` };
  return { key: "far", cls: "text-slate-600", label: `${diffDays}d` };
}

/** Compute derived fields on a loan (equity_value, gross_proceeds, cash_flow).
 *  Cash-flow uses the user-entered Monthly Payment directly — no synthetic
 *  monthly-interest number is derived.
 */
function derive(loan) {
  const num = (v) => (v == null || v === "" ? 0 : Number(v) || 0);
  const equity = num(loan.current_value) - num(loan.principal);
  const gross_proceeds = num(loan.sale_amount) - num(loan.release_amount);
  const cash_flow = num(loan.monthly_revenue) - num(loan.monthly_expense) - num(loan.monthly_tax) - num(loan.monthly_payment);
  // Auto-compute term in months from (loan_start_date → maturity_date). Falls
  // back to a stored value if either date is missing so older rows still show.
  let term_months = loan.term_months ?? null;
  if (loan.loan_start_date && loan.maturity_date) {
    const s = new Date(String(loan.loan_start_date).slice(0, 10) + "T00:00:00");
    const m = new Date(String(loan.maturity_date).slice(0, 10) + "T00:00:00");
    if (!Number.isNaN(s.getTime()) && !Number.isNaN(m.getTime()) && m >= s) {
      const years = m.getFullYear() - s.getFullYear();
      const months = m.getMonth() - s.getMonth();
      const dayDelta = m.getDate() - s.getDate();
      term_months = years * 12 + months + (dayDelta < 0 ? -1 : 0);
      // Fractional months for partial periods
      const anchor = new Date(s); anchor.setMonth(anchor.getMonth() + term_months);
      const remainderMs = m - anchor;
      const dayMs = 24 * 3600 * 1000;
      const monthApprox = 30.4375;
      const frac = remainderMs / (dayMs * monthApprox);
      term_months = Math.round((term_months + frac) * 10) / 10;
    }
  }
  return { equity_value: equity, gross_proceeds, cash_flow, term_months };
}

function renderCell(loan, col) {
  const d = derive(loan);
  let v;
  if (col.custom) {
    v = loan?.custom_fields?.[col.id];
  } else if (col.calc) {
    v = d[col.id];
  } else {
    v = loan?.[col.id];
  }
  if (col.id === "maturity_date") {
    const tone = maturityTone(v);
    return (
      <div className="flex items-center gap-1.5">
        <span>{fmtDate(v)}</span>
        {tone && <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${tone.cls}`}>{tone.label}</span>}
      </div>
    );
  }
  if (col.type === "money") return fmtMoney(v);
  if (col.type === "percent") return fmtPct(v);
  if (col.type === "date") return fmtDate(v);
  if (col.type === "number") return v == null || v === "" ? "" : String(v);
  return v || "";
}

function inputForType(col, value, onChange, testId, suggestions) {
  const commonCls = "w-full px-1.5 py-0.5 border border-slate-300 rounded text-xs bg-white";
  if (col.calc) {
    return <span className="text-[10px] text-slate-400 italic px-1">(auto)</span>;
  }
  if (col.type === "money" || col.type === "number" || col.type === "percent") {
    return <input type="number" step="any" className={commonCls} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={testId} />;
  }
  if (col.type === "date") {
    return <input type="date" className={commonCls} value={value ? String(value).slice(0, 10) : ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testId} />;
  }
  const listId = col.autocomplete && suggestions?.length ? `datalist-${col.id}` : undefined;
  return (
    <>
      <input type="text" list={listId} className={commonCls} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testId} autoComplete="off" />
      {listId && (
        <datalist id={listId}>
          {suggestions.map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
    </>
  );
}

// --------- Draggable column header ---------
function DraggableHeader({ col, sortBy, onSort, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const active = sortBy?.key === col.id;
  return (
    <th ref={setNodeRef} style={style} data-col-id={col.id} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap bg-white select-none group">
      <div className="flex items-center gap-1">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-600" title="Drag to reorder column" data-testid={`col-drag-${col.id}`}>
          <DotsSixVertical size={12} weight="bold" />
        </button>
        <button
          onClick={() => onSort && onSort(col.id)}
          className={`inline-flex items-center gap-1 hover:text-[#327de3] ${active ? "text-[#327de3]" : ""}`}
          data-testid={`col-sort-${col.id}`}
          title="Sort by this column"
        >
          <span>{col.label}</span>
          <ArrowsDownUp size={10} className={active ? "text-[#327de3]" : "text-slate-300"} weight={active ? "bold" : "regular"} />
        </button>
        {col.custom && onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(col); }}
            className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-rose-600"
            title={`Delete "${col.label}" column`}
            data-testid={`col-delete-${col.id}`}
          >
            <X size={11} weight="bold" />
          </button>
        )}
      </div>
    </th>
  );
}

export default function LoansPage() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null); // null=loading, {}=result
  const [loans, setLoans] = useState([]);
  const [search, setSearch] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [newRow, setNewRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openLoan, setOpenLoan] = useState(null);
  const [reminderMode, setReminderMode] = useState(false); // when true, table gets a "reminder flag" checkbox column
  const [reminderCfgOpen, setReminderCfgOpen] = useState(false);
  const [rates, setRates] = useState({ SOFR: null, DPRIME: null });
  const [suggestions, setSuggestions] = useState({});
  const [showDeleted, setShowDeleted] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pdfPickerOpen, setPdfPickerOpen] = useState(false);
  const [pdfCols, setPdfCols] = useState(null);
  const [toolbarOpen, setToolbarOpen] = useState(false);       // hamburger toolbar collapse
  const [sortBy, setSortBy] = useState({ key: "collateral", dir: "asc" });
  const [customKpiMode, setCustomKpiMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [customKpiActive, setCustomKpiActive] = useState(false); // true when "refresh" clicked → KPIs use selectedRows
  const [ratesLoading, setRatesLoading] = useState(false);
  const [customCols, setCustomCols] = useState([]);              // extra user-defined columns from the backend
  const [addColOpen, setAddColOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [pendingDeleteCol, setPendingDeleteCol] = useState(null); // {id,label} while confirming
  const RATES_CACHE_KEY = "strata.loans.ratesCache.v1";
  // KPI card ordering (drag-reorderable, sticky per user in localStorage).
  const DEFAULT_KPI_ORDER = ["curr", "principal", "equity", "arv", "monthly", "blended", "cashflow"];
  const [kpiOrder, setKpiOrder] = useState(() => {
    try {
      const s = localStorage.getItem(KPI_ORDER_LS_KEY);
      if (s) {
        const parsed = JSON.parse(s);
        const known = new Set(DEFAULT_KPI_ORDER);
        const merged = parsed.filter((id) => known.has(id));
        for (const k of DEFAULT_KPI_ORDER) if (!merged.includes(k)) merged.push(k);
        return merged;
      }
    } catch { /* ignore */ }
    return DEFAULT_KPI_ORDER;
  });
  useEffect(() => {
    localStorage.setItem(KPI_ORDER_LS_KEY, JSON.stringify(kpiOrder));
  }, [kpiOrder]);
  const [colOrder, setColOrder] = useState(() => {
    try {
      const s = localStorage.getItem(COL_ORDER_LS_KEY);
      if (s) {
        const parsed = JSON.parse(s);
        // Merge stored order with any newly-introduced columns.
        const known = new Set(DEFAULT_COLS.map((c) => c.id));
        const merged = parsed.filter((id) => known.has(id) || id.startsWith("custom_"));
        for (const c of DEFAULT_COLS) if (!merged.includes(c.id)) merged.push(c.id);
        return merged;
      }
    } catch { /* ignore */ }
    return DEFAULT_COLS.map((c) => c.id);
  });
  const tableWrapRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(COL_ORDER_LS_KEY, JSON.stringify(colOrder));
  }, [colOrder]);

  // Full merged column list — includes user-defined custom columns marked
  // with ``custom: true`` (their ``id`` == backend ``key`` so the same
  // lookup works everywhere).
  const ALL_COLS = useMemo(() => {
    const custom = customCols.map((c) => ({
      id: c.key,
      label: c.label,
      type: c.type,
      custom: true,
      custom_id: c.id,
    }));
    return [...DEFAULT_COLS, ...custom];
  }, [customCols]);

  // Whenever the set of known columns changes, ensure every column id is
  // present in the persisted order (append missing at the end, drop stale).
  useEffect(() => {
    const known = new Set(ALL_COLS.map((c) => c.id));
    setColOrder((prev) => {
      const filtered = prev.filter((id) => known.has(id));
      for (const c of ALL_COLS) if (!filtered.includes(c.id)) filtered.push(c.id);
      // Only update if something actually changed to avoid a render loop.
      if (filtered.length === prev.length && filtered.every((v, i) => v === prev[i])) return prev;
      return filtered;
    });
  }, [ALL_COLS]);

  const columns = useMemo(
    () => colOrder.map((id) => ALL_COLS.find((c) => c.id === id)).filter(Boolean),
    [colOrder, ALL_COLS],
  );

  // Access probe.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/loans/access");
        setAccess(data);
      } catch {
        setAccess({ can_view: false });
      }
    })();
  }, []);

  const loadLoans = useCallback(async () => {
    try {
      // ``deleted=any`` — include soft-deleted rows so the "Deleted loans"
      // section can render them. Backend supports ``deleted=true|false``,
      // omitting the param altogether defaults to hiding them, so we fetch
      // twice and merge.
      const [active, dead] = await Promise.all([
        api.get("/loans"),                        // deleted != true
        api.get("/loans?deleted=true"),
      ]);
      setLoans([...(active.data || []), ...(dead.data || [])]);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  }, []);

  const loadSuggestions = useCallback(async () => {
    const fields = ["collateral", "loan_type_class", "lender", "borrower", "rate_type"];
    const results = await Promise.all(fields.map((f) =>
      api.get(`/loans/field-values?field=${f}`).then((r) => [f, r.data]).catch(() => [f, []])
    ));
    setSuggestions(Object.fromEntries(results));
  }, []);

  const loadCustomCols = useCallback(async () => {
    try {
      const { data } = await api.get("/loans/columns");
      setCustomCols(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (access?.can_view) { loadLoans(); loadSuggestions(); loadCustomCols(); } }, [access?.can_view, loadLoans, loadSuggestions, loadCustomCols]);

  const loadRates = useCallback(async () => {
    // 1) Prime from localStorage cache immediately so the KPIs never show "—".
    try {
      const cached = JSON.parse(localStorage.getItem(RATES_CACHE_KEY) || "null");
      if (cached && (cached.SOFR || cached.DPRIME)) setRates(cached);
    } catch { /* ignore */ }
    setRatesLoading(true);
    try {
      // 2) Try the backend, which itself auto-refreshes from FRED when stale.
      const { data } = await api.get("/loans/rates");
      // Merge — never null-out an existing series just because THIS response
      // didn't return it (the "hover said updated but card shows nothing" bug).
      setRates((prev) => {
        const merged = { ...(prev || {}) };
        for (const k of Object.keys(data || {})) {
          if (data[k]) merged[k] = data[k];
        }
        try { localStorage.setItem(RATES_CACHE_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
        return merged;
      });
    } catch { /* ignore — fall back to cached value */ }
    finally { setRatesLoading(false); }
  }, []);
  useEffect(() => { if (access?.can_view) loadRates(); }, [access?.can_view, loadRates]);

  const refreshRates = async () => {
    setRatesLoading(true);
    try {
      const { data } = await api.post("/loans/rates/refresh");
      setRates((prev) => {
        const merged = { ...(prev || {}) };
        for (const k of Object.keys(data || {})) {
          if (data[k]) merged[k] = data[k];
        }
        try { localStorage.setItem(RATES_CACHE_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
        return merged;
      });
      toast.success("Rates refreshed from FRED");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setRatesLoading(false); }
  };

  const active = useMemo(() => loans.filter((l) => !l.archived && !l.deleted), [loans]);
  const archived = useMemo(() => loans.filter((l) => l.archived && !l.deleted), [loans]);
  const deleted = useMemo(() => loans.filter((l) => l.deleted), [loans]);

  // Filter by search across every stringifiable column, including custom
  // column values entered by the user.
  const filter = useCallback((list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((l) => {
      const hay = [
        l.loan_id, l.collateral, l.loan_type_class, l.lender, l.borrower, l.rate_type,
        l.current_value, l.arv, l.principal, l.construction_holdback,
        l.origination_points, l.extension_points, l.release_amount, l.sale_amount, l.purchase_price,
        l.term_months, l.maturity_date,
        l.interest_rate, l.step_up_date, l.step_up_rate, l.balloon_payment, l.monthly_payment,
        ...Object.values(l.custom_fields || {}),
      ].map((v) => (v == null ? "" : String(v))).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [search]);

  const _applySort = useCallback((list) => {
    if (!sortBy.key) return list;
    const col = ALL_COLS.find((c) => c.id === sortBy.key);
    const arr = [...list];
    arr.sort((a, b) => {
      let av = col?.custom ? (a.custom_fields || {})[sortBy.key]
             : col?.calc  ? derive(a)[sortBy.key]
             : a[sortBy.key];
      let bv = col?.custom ? (b.custom_fields || {})[sortBy.key]
             : col?.calc  ? derive(b)[sortBy.key]
             : b[sortBy.key];
      if (av == null || av === "") return sortBy.dir === "asc" ? 1 : -1;
      if (bv == null || bv === "") return sortBy.dir === "asc" ? -1 : 1;
      if (typeof av === "number" || (!isNaN(Number(av)) && col?.type !== "text")) {
        av = Number(av); bv = Number(bv);
      } else {
        av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
      }
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return sortBy.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [sortBy, ALL_COLS]);

  const activeFiltered = useMemo(() => _applySort(filter(active)), [active, filter, _applySort]);
  const archivedFiltered = useMemo(() => _applySort(filter(archived)), [archived, filter, _applySort]);
  const deletedFiltered = useMemo(() => _applySort(filter(deleted)), [deleted, filter, _applySort]);

  // KPI aggregates over the ACTIVE (non-archived, non-deleted) list, or the
  // Custom-KPI-selected subset when the user has activated that mode.
  const kpi = useMemo(() => {
    const scope = customKpiActive
      ? active.filter((l) => selectedRows.has(l.id))
      : active;
    // Deduplicate current_value by collateral so multiple loans on the same
    // asset don't double-count the asset's market value.
    const seenCollateral = new Map();
    let dedupedCurr = 0;
    let dedupedEquity = 0;
    for (const l of scope) {
      const key = (l.collateral || "").trim().toLowerCase();
      if (!key) {
        dedupedCurr += Number(l.current_value) || 0;
      } else if (!seenCollateral.has(key)) {
        seenCollateral.set(key, Number(l.current_value) || 0);
        dedupedCurr += Number(l.current_value) || 0;
      }
    }
    const totalArv = scope.reduce((s, l) => s + (Number(l.arv) || 0), 0);
    const totalPrincipal = scope.reduce((s, l) => s + (Number(l.principal) || 0), 0);
    dedupedEquity = dedupedCurr - totalPrincipal;
    const totalMonthly = scope.reduce((s, l) => s + (Number(l.monthly_payment) || 0), 0);
    const monthlyAvg = scope.length ? totalMonthly / scope.length : 0;
    const weighted = scope.reduce((s, l) => s + (Number(l.principal) || 0) * (Number(l.interest_rate) || 0), 0);
    const blended = totalPrincipal > 0 ? weighted / totalPrincipal : 0;
    const globalCashFlow = scope.reduce((s, l) => s + derive(l).cash_flow, 0);
    return {
      totalCurr: dedupedCurr,
      totalArv,
      totalPrincipal,
      equityValue: dedupedEquity,
      monthlyAvg,
      blended,
      globalCashFlow,
      ltv: dedupedCurr > 0 ? (totalPrincipal / dedupedCurr) * 100 : 0,
      ltarv: totalArv > 0 ? (totalPrincipal / totalArv) * 100 : 0,
      scopeCount: scope.length,
    };
  }, [active, customKpiActive, selectedRows]);

  // ---- Inline new-row add ----
  const startAddRow = () => {
    setEditMode(true);
    setNewRow({
      collateral: "", loan_type_class: "", lender: "", borrower: "",
      current_value: null, arv: null, principal: null,
      construction_holdback: null, origination_points: null, extension_points: null,
      release_amount: null, sale_amount: null, purchase_price: null,
      term_months: null,
      rate_type: "", maturity_date: null,
      interest_rate: null, step_up_date: null, step_up_rate: null,
      balloon_payment: null, monthly_payment: null,
    });
    setTimeout(() => {
      tableWrapRef.current?.querySelector('[data-testid="loans-new-row-collateral"]')?.focus?.();
    }, 40);
  };

  const cancelAdd = () => { setNewRow(null); setEditMode(false); };

  const saveNewRow = async () => {
    if (!newRow) return;
    setSaving(true);
    try {
      const { data } = await api.post("/loans", newRow);
      toast.success(`Loan #${data.loan_id} added`);
      setLoans((prev) => [...prev, data].sort((a, b) => (a.loan_id || 0) - (b.loan_id || 0)));
      setNewRow(null);
      setEditMode(false);
      loadSuggestions();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async (loan) => {
    if (!window.confirm(`Move loan #${loan.loan_id} to the Deleted Loans section?`)) return;
    try {
      await api.delete(`/loans/${loan.id}`);
      setLoans((prev) => prev.map((l) => (l.id === loan.id ? { ...l, deleted: true } : l)));
      toast.success("Loan moved to Deleted Loans");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const restoreDeleted = async (loan) => {
    try {
      const { data } = await api.patch(`/loans/${loan.id}`, { field: "deleted", value: false });
      setLoans((prev) => prev.map((l) => (l.id === loan.id ? data : l)));
      toast.success("Loan restored");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const hardDelete = async (loan) => {
    if (!window.confirm(`Permanently delete loan #${loan.loan_id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/loans/${loan.id}?hard=true`);
      setLoans((prev) => prev.filter((l) => l.id !== loan.id));
      toast.success("Loan permanently deleted");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const toggleArchive = async (loan) => {
    try {
      const { data } = await api.patch(`/loans/${loan.id}`, { field: "archived", value: !loan.archived });
      setLoans((prev) => prev.map((l) => (l.id === loan.id ? data : l)));
      toast.success(loan.archived ? "Loan restored to active" : "Loan archived");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const toggleReminderFlag = async (loan) => {
    try {
      const { data } = await api.patch(`/loans/${loan.id}`, { field: "email_reminder_enabled", value: !loan.email_reminder_enabled });
      setLoans((prev) => prev.map((l) => (l.id === loan.id ? data : l)));
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  // ---- DnD column reorder ----
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setColOrder((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const onKpiDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setKpiOrder((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  // ---- Export ----
  const _exportStamp = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yy = String(now.getFullYear()).slice(-2);
    const time = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return {
      filename: `${mm}.${dd}.${yy}`,
      header: `Exported On ${mm}/${dd}/${yy} ${time}`,
    };
  };

  const exportExcel = () => {
    const { filename, header } = _exportStamp();
    const wb = XLSX.utils.book_new();
    // AoA with typed cells. Row 1 = "Exported On …". Row 2 blank. Row 3 headers.
    const aoa = [
      [{ v: `LOANS · STRATA · ${header}`, s: { font: { bold: true } } }],
      [""],
      ["Loan ID", ...columns.map((c) => c.label)],
    ];
    for (const l of [...active, ...archived, ...deleted]) {
      const d = derive(l);
      const row = [l.loan_id];
      for (const c of columns) {
        let v = l[c.id];
        if (c.calc) v = d[c.id];
        if (v == null || v === "") { row.push(""); continue; }
        if (c.type === "money") {
          row.push({ v: Number(v), t: "n", z: '"$"#,##0.00_);("$"#,##0.00);"-"' });
        } else if (c.type === "percent") {
          row.push({ v: Number(v) / 100, t: "n", z: "0.000%" });
        } else if (c.type === "date") {
          row.push({ v: v.slice(0, 10), t: "d", z: "m/d/yy" });
        } else if (c.type === "number") {
          row.push({ v: Number(v), t: "n" });
        } else {
          row.push(String(v));
        }
      }
      aoa.push(row);
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });
    // Column widths.
    ws["!cols"] = [{ wch: 8 }, ...columns.map((c) => ({ wch: c.type === "text" ? 22 : 14 }))];
    // Merge the Exported-On header across all columns.
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columns.length } }];
    XLSX.utils.book_append_sheet(wb, ws, "Loans");
    XLSX.writeFile(wb, `LOANS STRATA excel_${filename}.xlsx`);
  };

  const exportPdf = (selectedColIds) => {
    const el = document.getElementById("loans-export-root");
    if (!el) { toast.error("Nothing to export"); return; }
    const { filename, header } = _exportStamp();
    // If a subset was chosen, temporarily hide the other columns via a
    // scoped stylesheet so the printed PDF only contains the picked ones.
    let styleEl = null;
    if (selectedColIds && selectedColIds.length) {
      const hide = colOrder.filter((id) => !selectedColIds.includes(id));
      if (hide.length) {
        const rules = hide.map((id) => `#loans-export-root [data-col-id="${id}"] { display: none !important; }`).join("\n");
        styleEl = document.createElement("style");
        styleEl.textContent = rules;
        document.head.appendChild(styleEl);
      }
    }
    // Pale-red "Exported On" banner, top-left of the printed page.
    const banner = document.createElement("div");
    banner.setAttribute("data-export-banner", "true");
    banner.style.cssText = "font-size:10px;color:#f87171;padding:4px 8px 6px;letter-spacing:1.2px;text-transform:uppercase;font-weight:600;text-align:left;";
    banner.textContent = header;
    el.prepend(banner);
    // Original fixed-scale export (renders cleaner than dynamic scale).
    html2pdf().from(el).set({
      margin: [0.25, 0.25, 0.25, 0.25],
      filename: `LOANS STRATA PDF_${filename}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: [11, 17], orientation: "landscape" },
    }).save().then(() => {
      banner.remove();
      if (styleEl) styleEl.remove();
    }).catch(() => {
      banner.remove();
      if (styleEl) styleEl.remove();
    });
  };

  const openPdfPicker = () => {
    // Default to whatever's currently visible in the user's column order.
    setPdfCols(colOrder.slice());
    setPdfPickerOpen(true);
  };

  if (access === null) {
    return (
      <div className="min-h-screen bg-[#F4F6F8]">
        <AppHeader />
        <div className="flex items-center justify-center py-32 text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!access?.can_view) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <AppHeader />
      <main className="max-w-none mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10 py-3" data-testid="loans-page">
        {/* Top bar */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <h1 className="text-xl font-bold text-slate-900 mr-2">Assets</h1>
          <div className="relative flex-1 min-w-0 max-w-[520px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by collateral, lender, borrower, ID, rate…" className="pl-9 bg-white" data-testid="loans-search" />
          </div>
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => setToolbarOpen((s) => !s)} data-testid="loans-toolbar-toggle" title="More actions">
              <ListIcon size={14} className="mr-1" />
              {toolbarOpen ? <CaretUp size={11} /> : <CaretDown size={11} />}
            </Button>
          </div>
        </div>

        {/* Collapsible action toolbar */}
        {toolbarOpen && (
          <div className="mb-3 rounded-lg border border-slate-200 bg-white p-2 flex flex-wrap gap-1.5" data-testid="loans-toolbar-panel">
            <div className="inline-flex items-center">
              <Button
                size="sm"
                variant={customKpiMode ? "default" : "outline"}
                onClick={() => {
                  if (customKpiMode) {
                    setCustomKpiMode(false);
                    setCustomKpiActive(false);
                    setSelectedRows(new Set());
                  } else {
                    setCustomKpiMode(true);
                  }
                }}
                className={customKpiMode ? "bg-orange-500 hover:bg-orange-600 rounded-r-none" : "border-orange-300 text-orange-700 hover:bg-orange-50 rounded-r-none border-r-0"}
                data-testid="loans-custom-kpi"
                title="Toggle Custom KPI mode — pick rows to filter the KPI cards"
              >
                Custom KPI
              </Button>
              <Button
                size="sm"
                variant={customKpiActive ? "default" : "outline"}
                onClick={() => {
                  if (!customKpiMode) return;
                  setCustomKpiActive(true);
                }}
                disabled={!customKpiMode || selectedRows.size === 0}
                className={`rounded-l-none -ml-px ${customKpiActive ? "bg-orange-600 hover:bg-orange-700" : "border-orange-300 text-orange-700 hover:bg-orange-50"}`}
                data-testid="loans-custom-kpi-refresh"
                title="Refresh KPI cards using only the checked rows"
              >
                <ArrowClockwise size={13} className="mr-1" /> Refresh
              </Button>
            </div>
            <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="loans-bulk-open">
              <FileXls size={14} className="mr-1.5" /> Bulk upload
            </Button>
            <Button size="sm" variant={editMode ? "default" : "outline"} onClick={editMode ? cancelAdd : startAddRow} data-testid="loans-toggle-edit" className={editMode ? "bg-[#327de3] hover:bg-[#1f64c2]" : ""}>
              <PencilSimple size={14} className="mr-1.5" /> {editMode ? "Exit edit mode" : "Add / edit mode"}
            </Button>
            <Button size="sm" variant="outline" onClick={exportExcel} data-testid="loans-export-excel">
              <FileXls size={14} className="mr-1.5" /> Export Excel
            </Button>
            <Button size="sm" variant="outline" onClick={openPdfPicker} data-testid="loans-export-pdf">
              <FilePdf size={14} className="mr-1.5" /> Export PDF
            </Button>
            <Button size="sm" variant={reminderMode ? "default" : "outline"} onClick={() => setReminderMode((m) => !m)} data-testid="loans-reminder-mode" className={reminderMode ? "bg-amber-500 hover:bg-amber-600" : ""}>
              <Bell size={14} className="mr-1.5" /> {reminderMode ? "Exit reminder mode" : "Reminder flags"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setReminderCfgOpen(true)} data-testid="loans-email-reminders">
              <Bell size={14} className="mr-1.5" /> Email reminders
            </Button>
          </div>
        )}

        {/* Big KPI cards */}
        <div id="loans-export-root">
        {/* Big KPI cards — drag-reorderable per user, sticky in localStorage */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onKpiDragEnd}>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 mb-2">
            <SortableContext items={kpiOrder} strategy={horizontalListSortingStrategy}>
              {kpiOrder.map((id) => {
                const def = KPI_DEFS[id];
                if (!def) return null;
                const props = def(kpi, active);
                return <SortableKpiCard key={id} id={id} {...props} />;
              })}
            </SortableContext>
          </div>
        </DndContext>

        {/* Mini rate cards (live from FRED) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <MiniRateCard testId="mini-wsj-prime" title="WSJ Prime Rate" rate={rates?.DPRIME} loading={ratesLoading} onRefresh={refreshRates} />
          <MiniRateCard testId="mini-sofr" title="SOFR" rate={rates?.SOFR} loading={ratesLoading} onRefresh={refreshRates} />
          <MaturityLegend />
        </div>

        {/* Active table */}
        <div className="flex items-center justify-between mb-1">
          <SectionHeader label={`Active assets/loans (${activeFiltered.length}${search ? ` of ${active.length}` : ""})`} />
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddColOpen(true)}
              className="text-[11px] h-7 px-2 border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="loans-add-column"
              title="Add a custom column to the table"
            >
              <Plus size={11} className="mr-1" /> Add column
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRestoreOpen(true)}
              className="text-[11px] h-7 px-2 border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="loans-restore-columns"
              title="Restore a previously-deleted custom column (data preserved)"
            >
              <ArrowClockwise size={11} className="mr-1" /> Restore custom columns
            </Button>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto" ref={tableWrapRef}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <table className="w-full text-xs" data-testid="loans-table-active">
              <thead>
                <tr className="bg-slate-50">
                  {customKpiMode && <th className="text-[10px] uppercase tracking-widest font-bold text-orange-700 px-2 py-1.5 border-b border-slate-200 bg-orange-100 sticky left-0 z-10">✓</th>}
                  <th className={`text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 sticky ${customKpiMode ? "left-10" : "left-0"} bg-slate-50 z-10`}>Loan ID</th>
                  <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap" title="Last Updated">LU</th>
                  <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap text-center">Attach.</th>
                  {reminderMode && <th className="text-[10px] uppercase tracking-widest font-bold text-amber-600 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap"><Bell size={11} className="inline" /> Reminder</th>}
                  <SortableContext items={colOrder} strategy={horizontalListSortingStrategy}>
                    {columns.map((c) => <DraggableHeader key={c.id} col={c} sortBy={sortBy} onSort={(k) => setSortBy((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))} onDelete={(col) => setPendingDeleteCol(col)} />)}
                  </SortableContext>
                  <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {editMode && newRow && (
                  <tr className="bg-[#eaf2ff]/40" data-testid="loans-new-row">
                    {customKpiMode && <td className="px-2 py-0.5 border-b border-slate-100"></td>}
                    <td className="px-2 py-0.5 border-b border-slate-100 text-[11px] font-bold text-slate-500 sticky left-0 bg-[#eaf2ff]/60">NEW</td>
                    <td className="px-2 py-0.5 border-b border-slate-100"></td>
                    <td className="px-2 py-0.5 border-b border-slate-100"></td>
                    {reminderMode && <td className="px-2 py-0.5 border-b border-slate-100"></td>}
                    {columns.map((c) => (
                      <td key={c.id} data-col-id={c.id} className="px-1.5 py-0.5 border-b border-slate-100 min-w-[110px]">
                        {c.custom
                          ? inputForType(
                              c,
                              (newRow.custom_fields || {})[c.id],
                              (v) => setNewRow((r) => ({ ...r, custom_fields: { ...(r.custom_fields || {}), [c.id]: v } })),
                              `loans-new-row-${c.id}`,
                              undefined,
                            )
                          : inputForType(
                              c,
                              newRow[c.id],
                              (v) => setNewRow((r) => ({ ...r, [c.id]: v })),
                              `loans-new-row-${c.id}`,
                              suggestions[c.id],
                            )}
                      </td>
                    ))}
                    <td className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap">
                      <Button size="sm" onClick={saveNewRow} disabled={saving} className="h-6 px-2 text-[10px] bg-[#327de3] hover:bg-[#1f64c2]" data-testid="loans-new-row-save">Save</Button>
                      <Button size="sm" variant="ghost" onClick={cancelAdd} className="h-6 px-2 text-[10px] ml-1"><X size={11} /></Button>
                    </td>
                  </tr>
                )}
                {activeFiltered.map((l) => (
                  <LoanRow key={l.id} loan={l} columns={columns} onOpen={() => setOpenLoan(l)} onArchive={() => toggleArchive(l)} reminderMode={reminderMode} onToggleReminder={() => toggleReminderFlag(l)} customKpiMode={customKpiMode} selected={selectedRows.has(l.id)} onToggleSelect={() => setSelectedRows((s) => { const n = new Set(s); if (n.has(l.id)) n.delete(l.id); else n.add(l.id); return n; })} />
                ))}
                {activeFiltered.length === 0 && !newRow && (
                  <tr>
                    <td colSpan={columns.length + 4 + (reminderMode ? 1 : 0) + (customKpiMode ? 1 : 0)} className="text-center text-slate-400 italic py-6 text-xs">
                      {search ? "No loans match your search." : "No loans yet. Click \"Add / edit mode\" then Save to add your first row."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </DndContext>
        </div>

        {/* Archived */}
        <div className="mt-4">
          <SectionHeader label={`Archived assets/loans (${archivedFiltered.length}${search ? ` of ${archived.length}` : ""})`} />
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 overflow-x-auto">
            <table className="w-full text-xs" data-testid="loans-table-archived">
              <thead>
                <tr className="bg-slate-100/70">
                  <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200">Loan ID</th>
                  <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap" title="Last Updated">LU</th>
                  <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap text-center">Attach.</th>
                  {columns.map((c) => <th key={c.id} data-col-id={c.id} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap">{c.label}</th>)}
                  <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {archivedFiltered.map((l) => (
                  <LoanRow key={l.id} loan={l} columns={columns} onOpen={() => setOpenLoan(l)} onArchive={() => toggleArchive(l)} onDelete={() => softDelete(l)} dim />
                ))}
                {archivedFiltered.length === 0 && (
                  <tr><td colSpan={columns.length + 4} className="text-center text-slate-400 italic py-4 text-xs">{search ? "No archived loans match." : "No archived loans."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Deleted Loans — minimalist collapsible section (excluded from KPIs). */}
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowDeleted((s) => !s)}
            className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
            data-testid="loans-deleted-toggle"
          >
            <span className={`inline-block transition-transform ${showDeleted ? "rotate-90" : ""}`}>▸</span>
            Deleted assets/loans ({deletedFiltered.length}{search ? ` of ${deleted.length}` : ""})
          </button>
          {showDeleted && (
            <div className="mt-1 rounded-md border border-slate-200 bg-slate-50/40 overflow-x-auto">
              <table className="w-full text-[10px]" data-testid="loans-table-deleted">
                <thead>
                  <tr className="bg-slate-100/50">
                    <th className="text-[9px] uppercase tracking-widest font-semibold text-slate-400 px-1.5 py-1 border-b border-slate-200">#</th>
                    <th className="text-[9px] uppercase tracking-widest font-semibold text-slate-400 px-1.5 py-1 border-b border-slate-200 whitespace-nowrap">Last Upd.</th>
                    <th className="text-[9px] uppercase tracking-widest font-semibold text-slate-400 px-1.5 py-1 border-b border-slate-200 whitespace-nowrap text-center">Att.</th>
                    {columns.map((c) => <th key={c.id} data-col-id={c.id} className="text-[9px] uppercase tracking-widest font-semibold text-slate-400 px-1.5 py-1 border-b border-slate-200 whitespace-nowrap">{c.label}</th>)}
                    <th className="text-[9px] uppercase tracking-widest font-semibold text-slate-400 px-1.5 py-1 border-b border-slate-200">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedFiltered.map((l) => (
                    <tr key={l.id} className="opacity-60 hover:opacity-100" data-testid={`loans-deleted-row-${l.id}`}>
                      <td className="px-1.5 py-0.5 border-b border-slate-100 font-semibold text-slate-500 whitespace-nowrap">#{l.loan_id}</td>
                      <td className="px-1.5 py-0.5 border-b border-slate-100 whitespace-nowrap text-slate-500">{l.updated_at ? new Date(l.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }) : ""}</td>
                      <td className="px-1.5 py-0.5 border-b border-slate-100 whitespace-nowrap text-center">{l.has_attachments ? <Paperclip size={11} className="inline text-slate-500" /> : <span className="inline-block w-4 h-2 bg-rose-200 border border-rose-300 rounded-sm" />}</td>
                      {columns.map((c) => (
                        <td key={c.id} data-col-id={c.id} className="px-1.5 py-0.5 border-b border-slate-100 whitespace-nowrap">{renderCell(l, c)}</td>
                      ))}
                      <td className="px-1.5 py-0.5 border-b border-slate-100 whitespace-nowrap">
                        <button onClick={() => restoreDeleted(l)} className="text-[9px] font-semibold text-emerald-700 hover:underline mr-1" data-testid={`loans-deleted-restore-${l.id}`}>Restore</button>
                        {user?.is_super_admin && (
                          <button onClick={() => hardDelete(l)} className="text-[9px] font-semibold text-rose-600 hover:underline" data-testid={`loans-deleted-hard-${l.id}`}>Purge</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {deletedFiltered.length === 0 && (
                    <tr><td colSpan={columns.length + 4} className="text-center text-slate-400 italic py-2 text-[10px]">No deleted loans.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>
      </main>

      <LoanModal
        loan={openLoan}
        customCols={customCols}
        onClose={() => { setOpenLoan(null); loadLoans(); }}
        onChange={(updated) => {
          setLoans((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
          setOpenLoan(updated);
        }}
      />

      <AddColumnModal
        open={addColOpen}
        onClose={() => setAddColOpen(false)}
        onCreated={() => loadCustomCols()}
      />

      <RestoreColumnsModal
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        onRestored={() => loadCustomCols()}
      />

      <Dialog open={!!pendingDeleteCol} onOpenChange={(o) => !o && setPendingDeleteCol(null)}>
        <DialogContent className="max-w-md" data-testid="loans-delete-column-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <WarningCircle size={18} weight="fill" /> Delete "{pendingDeleteCol?.label}" column?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p><strong>This action can't be undone from here.</strong> The column disappears from the table for everyone right away.</p>
            <p className="text-[12px] text-slate-500">
              However, all cell values across every row stay safe on the server. If you change your mind, use <em>Restore custom columns</em> to bring the column and its data back.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPendingDeleteCol(null)} data-testid="loans-delete-column-cancel">Cancel</Button>
            <Button
              onClick={async () => {
                const c = pendingDeleteCol;
                if (!c) return;
                try {
                  await api.delete(`/loans/columns/${c.custom_id}`);
                  toast.success(`Column "${c.label}" deleted. Values preserved.`);
                  setPendingDeleteCol(null);
                  loadCustomCols();
                } catch (e) {
                  const m = apiErrorMessage(e); if (m) toast.error(m);
                }
              }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="loans-delete-column-confirm"
            >
              Delete column
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReminderConfigModal
        open={reminderCfgOpen}
        onClose={() => setReminderCfgOpen(false)}
        isSuperAdmin={!!user?.is_super_admin}
      />

      <BulkUploadModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onImported={() => { loadLoans(); loadSuggestions(); }}
      />

      {/* PDF export column picker */}
      {pdfPickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="pdf-picker-modal" onClick={() => setPdfPickerOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 bg-gradient-to-r from-[#1f64c2] to-[#327de3] text-white flex items-center gap-2">
              <FilePdf size={18} />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest opacity-80">Export</div>
                <div className="text-base font-bold">Pick columns for the PDF</div>
              </div>
              <button onClick={() => setPdfPickerOpen(false)} className="p-1 rounded hover:bg-white/10"><X size={16} /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="text-[11px] text-slate-500 mb-2">
                Uncheck any columns you'd like to hide in the PDF so more data fits on one page. Loan ID and Actions are always included.
              </div>
              <div className="flex items-center gap-2 mb-2">
                <button onClick={() => setPdfCols(colOrder.slice())} className="text-[11px] font-semibold text-[#1f64c2] hover:underline" data-testid="pdf-picker-all">Select all</button>
                <span className="text-slate-300">·</span>
                <button onClick={() => setPdfCols([])} className="text-[11px] font-semibold text-[#1f64c2] hover:underline" data-testid="pdf-picker-none">Deselect all</button>
                <span className="ml-auto text-[10px] text-slate-500">{pdfCols?.length || 0} of {colOrder.length} selected</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {colOrder.map((id) => {
                  const c = DEFAULT_COLS.find((x) => x.id === id);
                  if (!c) return null;
                  const on = pdfCols?.includes(id);
                  return (
                    <label key={id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border cursor-pointer ${on ? "bg-[#eaf2ff] border-[#327de3]" : "bg-white border-slate-200"}`}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => setPdfCols((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))}
                        className="accent-[#327de3]"
                        data-testid={`pdf-picker-col-${id}`}
                      />
                      <span className="flex-1">{c.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setPdfPickerOpen(false)}>Cancel</Button>
              <Button
                onClick={() => { setPdfPickerOpen(false); setTimeout(() => exportPdf(pdfCols), 100); }}
                className="bg-[#327de3] hover:bg-[#1f64c2]"
                disabled={!pdfCols?.length}
                data-testid="pdf-picker-export"
              >
                <FilePdf size={13} className="mr-1.5" /> Export PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// KPI card definitions — each returns props for a card given (kpi, active).
const KPI_DEFS = {
  curr: (k) => ({
    testId: "kpi-current-value",
    title: "Current Asset Value",
    value: fmtMoney(k.totalCurr),
    sub: "Loan to Current Value",
    subValue: k.totalCurr > 0 ? `${k.ltv.toFixed(1)}%` : "—",
  }),
  principal: (k, active) => ({
    testId: "kpi-principal",
    title: "Total Loan Principal",
    value: fmtMoney(k.totalPrincipal),
    sub: `${active.length} active loan${active.length === 1 ? "" : "s"}`,
  }),
  equity: (k) => ({
    testId: "kpi-equity",
    title: "Equity Value",
    value: fmtMoney(k.equityValue),
    sub: "Current Value − Principal",
  }),
  arv: (k) => ({
    testId: "kpi-arv",
    title: "Finished Asset Value (ARV)",
    value: fmtMoney(k.totalArv),
    sub: "Loan to ARV",
    subValue: k.totalArv > 0 ? `${k.ltarv.toFixed(1)}%` : "—",
  }),
  monthly: (k) => ({
    testId: "kpi-monthly",
    title: "Monthly Payment (avg)",
    value: fmtMoney(k.monthlyAvg),
    sub: "Averaged across active loans",
  }),
  blended: (k) => ({
    testId: "kpi-blended",
    title: "Blended Interest Rate",
    value: k.totalPrincipal > 0 ? `${k.blended.toFixed(3)}%` : "—",
    sub: "Principal-weighted, annual",
  }),
  cashflow: (k) => ({
    testId: "kpi-global-cashflow",
    title: "Global Cash Flow",
    value: fmtMoney(k.globalCashFlow),
    sub: "Rev − Exp − Tax − Pmt, monthly",
    highlight: k.globalCashFlow >= 0 ? "positive" : "negative",
  }),
};

function KpiCard({ title, value, sub, subValue, testId, dragHandle, highlight }) {
  const pillCls =
    highlight === "positive" ? "border-emerald-300 bg-emerald-50" :
    highlight === "negative" ? "border-rose-300 bg-rose-50" :
    "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border ${pillCls} px-3 py-2 relative`} data-testid={testId}>
      {dragHandle}
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold pr-4">{title}</div>
      <div className={`text-lg font-bold leading-tight mt-0.5 ${highlight === "positive" ? "text-emerald-700" : highlight === "negative" ? "text-rose-700" : "text-slate-900"}`}>{value}</div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span className="text-[10px] text-slate-500">{sub}</span>
        {subValue != null && <span className="text-[11px] font-semibold text-[#1f64c2]">{subValue}</span>}
      </div>
    </div>
  );
}

function SortableKpiCard({ id, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const dragHandle = (
    <button {...attributes} {...listeners} className="absolute top-1.5 right-1.5 text-slate-300 hover:text-slate-600 cursor-grab active:cursor-grabbing" title="Drag to reorder" data-testid={`kpi-drag-${id}`}>
      <DotsSixVertical size={12} weight="bold" />
    </button>
  );
  return <div ref={setNodeRef} style={style}><KpiCard {...props} dragHandle={dragHandle} /></div>;
}

function MiniRateCard({ title, rate, onRefresh, loading, testId }) {
  const val = rate?.value != null ? `${Number(rate.value).toFixed(2)}%` : null;
  const when = rate?.observation_date ? `Updated last: ${rate.observation_date}` : "Updated last: —";
  return (
    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 flex items-start gap-2" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{title}</div>
        {loading && !val ? (
          <div className="mt-1 space-y-1" data-testid={`${testId}-skeleton`}>
            <div className="h-4 w-16 bg-slate-200 rounded animate-pulse" />
            <div className="h-2 w-24 bg-slate-100 rounded animate-pulse" />
          </div>
        ) : (
          <>
            <div className="text-base font-bold text-slate-900 leading-tight">{val || "—"}</div>
            <div className="text-[9px] text-rose-400 italic truncate">{when}</div>
          </>
        )}
      </div>
      {onRefresh && (
        <button
          onClick={onRefresh}
          className="text-slate-500 hover:text-[#1f64c2] p-1 rounded-full hover:bg-slate-100 transition-colors"
          title="Refresh from FRED"
          data-testid={`${testId}-refresh`}
          disabled={loading}
        >
          <ArrowClockwise size={18} weight="bold" className={loading ? "animate-spin" : ""} />
        </button>
      )}
    </div>
  );
}

function MaturityLegend() {
  return (
    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 text-[10px]" data-testid="maturity-legend">
      <div className="uppercase tracking-widest text-slate-500 font-semibold mb-1">Maturity legend</div>
      <div className="flex flex-wrap gap-1">
        <span className="bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-full font-semibold">≤30 d</span>
        <span className="bg-orange-100 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded-full font-semibold">≤60 d</span>
        <span className="bg-yellow-100 text-yellow-800 border border-yellow-200 px-1.5 py-0.5 rounded-full font-semibold">≤90 d</span>
        <span className="bg-rose-600 text-white px-1.5 py-0.5 rounded-full font-semibold">Expired</span>
      </div>
    </div>
  );
}

function SectionHeader({ label }) {
  return <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 mt-1">{label}</div>;
}

function LoanRow({ loan, columns, onOpen, onArchive, onDelete, dim, reminderMode, onToggleReminder, customKpiMode, selected, onToggleSelect }) {
  const archived = !!loan.archived && !loan.deleted;
  const lastUpd = (() => {
    if (!loan.updated_at) return "";
    const d = new Date(loan.updated_at);
    if (isNaN(d)) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}.${dd}.${yy}`;
  })();
  return (
    <tr
      className={`hover:bg-[#eaf2ff]/40 cursor-pointer ${dim ? "opacity-70" : ""} ${archived ? "bg-emerald-50/70" : ""} ${loan.email_reminder_enabled ? "bg-amber-50/40" : ""} ${selected ? "bg-orange-50" : ""}`}
      onClick={onOpen}
      data-testid={`loans-row-${loan.id}`}
    >
      {customKpiMode && (
        <td
          className={`px-2 py-0.5 border-b border-slate-100 sticky left-0 z-[2] ${selected ? "bg-orange-100" : "bg-orange-50"}`}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
            onClick={(e) => e.stopPropagation()}
            className="w-3.5 h-3.5 accent-orange-500 cursor-pointer"
            data-testid={`loans-select-${loan.id}`}
          />
        </td>
      )}
      <td className={`px-2 py-0.5 border-b border-slate-100 font-bold text-slate-700 sticky ${customKpiMode ? "left-10" : "left-0"} z-[1] whitespace-nowrap ${archived ? "bg-emerald-50" : "bg-white"}`}>
        #{loan.loan_id}
      </td>
      <td className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap text-[11px] text-slate-600" data-testid={`loans-row-updated-${loan.id}`}>
        {lastUpd}
      </td>
      <td className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap text-center" data-testid={`loans-row-attach-${loan.id}`}>
        {loan.has_attachments ? (
          <Paperclip size={14} weight="bold" className="inline text-slate-600" />
        ) : (
          <span className="inline-block w-5 h-3 bg-rose-200 border border-rose-300 rounded-sm" title="No attachments — please upload" />
        )}
      </td>
      {reminderMode && (
        <td className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={!!loan.email_reminder_enabled} onChange={onToggleReminder} className="w-3.5 h-3.5 accent-amber-500" data-testid={`loans-reminder-toggle-${loan.id}`} />
            {loan.email_reminder_enabled && <span className="text-[10px] font-bold uppercase text-amber-700">ON</span>}
          </label>
        </td>
      )}
      {columns.map((c) => (
        <td key={c.id} data-col-id={c.id} className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap">
          {renderCell(loan, c)}
        </td>
      ))}
      <td className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          <button
            onClick={onArchive}
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
              archived ? "bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-white border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-600"
            }`}
            data-testid={`loans-archive-${loan.id}`}
            title={archived ? "Restore to active" : "Archive this loan"}
          >
            <Archive size={10} className="inline mr-0.5" />
            {archived ? "Restore" : "Archive"}
          </button>
          {archived && onDelete && (
            <button
              onClick={onDelete}
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-white border-slate-200 text-slate-500 hover:border-rose-400 hover:text-rose-600"
              data-testid={`loans-delete-${loan.id}`}
              title="Move to Deleted Loans"
            >
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
